import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';

import Button from '@/components/goalbased/Questionarie/Button/Button';
import type {
  QuizButtonSectionRecordModelType,
  QuizInputSectionRecordModelType,
} from '@/models/api';
import { useStore } from '@/models/root-store';
import { MAX_WEIGHT, MIN_WEIGHT } from '@/utils/constants';

type Props = {
  heading: string;
  genderSection: QuizButtonSectionRecordModelType;
  ageSection: QuizButtonSectionRecordModelType;
  weightSection: QuizInputSectionRecordModelType;
};

const Profile: React.FC<Props> = observer(
  ({ heading, genderSection, ageSection, weightSection }) => {
    const weightRef: any = useRef(null);
    const { cartStore } = useStore();
    const { goalBased } = cartStore;
    const [ageError, setAgeError] = useState('');

    const handleWeight = (inputWeight: number) => {
      if (inputWeight >= MIN_WEIGHT && inputWeight <= MAX_WEIGHT) {
        goalBased.setPreset('weight', inputWeight);
        setAgeError('');
      } else if (
        inputWeight >= 0 &&
        (inputWeight < MIN_WEIGHT || inputWeight > MAX_WEIGHT)
      ) {
        setAgeError(
          inputWeight < MIN_WEIGHT
            ? `Minimum weight is ${MIN_WEIGHT}`
            : `Maximum weight is ${MAX_WEIGHT}`,
        );
      }
    };

    const onWheel = () => {
      weightRef?.current?.blur();
    };

    return (
      <div className="mb-14 mt-6 px-4 md:p-0">
        <div className="mb-8 font-interBold text-4xl leading-9 text-black md:mb-6">
          {heading}
        </div>
        <div className="mb-8">
          <h4 className="mb-4 font-interMedium text-base text-black">
            {genderSection?.questionText}
          </h4>
          <div className="flex">
            {genderSection?.quizAnswers &&
              genderSection?.quizAnswers.map((answer, i) => {
                return (
                  <Button
                    key={i}
                    theme={
                      goalBased.gender === answer.value
                        ? 'secondary'
                        : 'primary'
                    }
                    className={`${
                      goalBased.gender === answer.value
                        ? 'p-2.5 md:py-3.5'
                        : 'p-3 md:py-4'
                    } mr-4 w-[107px] justify-center font-interBold leading-4 last:mr-0`}
                    onClick={() => goalBased.setPreset('gender', answer.value)}
                  >
                    {_.capitalize(answer.name)}
                  </Button>
                );
              })}
          </div>
        </div>
        <div className="mb-8">
          <h4 className="mb-4 font-interMedium text-base text-black">
            {ageSection.questionText}
          </h4>
          <div className="w-[230px] md:w-full">
            <div className="grid grid-cols-2 gap-4 md:flex">
              {ageSection &&
                ageSection?.quizAnswers &&
                ageSection?.quizAnswers.map((ageRange) => {
                  return (
                    <Button
                      key={ageRange.id}
                      theme={
                        goalBased.ageRange === ageRange.value
                          ? 'secondary'
                          : 'primary'
                      }
                      className={`w-[107px] justify-center leading-4 ${
                        goalBased.ageRange === ageRange.value
                          ? 'px-2.5 py-3.5'
                          : 'px-3 py-4'
                      }`}
                      onClick={() =>
                        goalBased.setPreset('ageRange', ageRange.value)
                      }
                    >
                      <div className="font-interBold">{ageRange.name}</div>
                    </Button>
                  );
                })}
            </div>
          </div>
        </div>
        <div className="mb-8">
          <h4 className="mb-4 font-interMedium text-base text-black">
            {weightSection?.questionText}
          </h4>
          <div className="relative">
            <input
              ref={weightRef}
              type="number"
              id="floating_outlined"
              className="focus:border-black-600 focus:border-black-500 peer block w-32 appearance-none rounded-lg border border-black bg-transparent px-5 py-2 pt-4 font-interBold text-xl text-black focus:outline-none focus:ring-0"
              placeholder=" "
              min={45}
              max={400}
              onChange={(e) => handleWeight(parseInt(e.target.value, 10))}
              defaultValue={goalBased.weight}
              onWheel={() => onWheel()}
            />
            <label
              htmlFor="floating_outlined"
              className="peer-focus:text-black-600 peer-focus:text-black-500 absolute left-1 top-2 z-10 origin-[0] -translate-y-4 scale-75 bg-white px-2 text-sm duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2"
            >
              {weightSection?.inputName}
            </label>
          </div>
          <div className="mt-1 text-error">{ageError}</div>
        </div>
      </div>
    );
  },
);

export default Profile;
